import { useMemo, useState } from 'react'
import { Clock, Users } from 'lucide-react'
import { formatTimeFromMinutes, getDayOfWeekName, formatCurrency } from '@meiuca/web-foundation'
import type { VenueDetailDayUseComponent, DayUseSlot } from '../../../types/sdui-arena-detail'
import { modalitiesManager } from '@/managers/modalitiesManager'

interface VenueDetailDayUseProps {
  config: VenueDetailDayUseComponent
}

export function VenueDetailDayUse({ config }: VenueDetailDayUseProps) {
  const { title, modalities } = config

  const data = modalities

  if (!data || data.length === 0) {
    return null
  }

  const [activeModality, setActiveModality] = useState<string | null>(null)

  const modalityOptions = useMemo(() => data.map(modality => modality.modality), [data])
  const selectedModality = useMemo(() => {
    const fallback = modalityOptions[0] ?? null
    if (!activeModality || !modalityOptions.includes(activeModality)) {
      return fallback
    }
    return activeModality
  }, [activeModality, modalityOptions])

  const selectedModalityData = useMemo(() => {
    return data.find(modality => modality.modality === selectedModality) ?? null
  }, [data, selectedModality])

  const weekdayGroups = useMemo(() => {
    if (!selectedModalityData) {
      return []
    }
    return [...selectedModalityData.weekdays].sort((a, b) => a.weekday - b.weekday)
  }, [selectedModalityData])

  const renderSlot = (slot: DayUseSlot) => (
    <div
      key={slot.id}
      className="group relative overflow-hidden rounded-lg border border-border-light bg-white hover:border-brand-primary hover:shadow-md transition-all duration-200"
    >
      <div className="p-4">
        <div className="flex items-start justify-between gap-4">
          {/* Horário - destaque principal */}
          <div className="flex items-center gap-2.5">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-brand-primary/10 text-brand-primary group-hover:bg-brand-primary group-hover:text-white transition-colors">
              <Clock className="h-5 w-5" />
            </div>
            <div>
              <div className="text-sm font-semibold text-text-primary">
                {formatTimeFromMinutes(slot.startTimeMinutes)} - {formatTimeFromMinutes(slot.endTimeMinutes)}
              </div>
              <div className="text-xs text-text-secondary mt-0.5">
                {Math.round((slot.endTimeMinutes - slot.startTimeMinutes) / 60)}h de duração
              </div>
            </div>
          </div>

          {/* Preço - destaque secundário */}
          <div className="text-right">
            <div className="text-lg font-bold text-brand-primary">
              {formatCurrency(slot.pricePerPersonCents)}
            </div>
            <div className="text-xs text-text-secondary">por pessoa</div>
          </div>
        </div>

        {/* Participantes */}
        <div className="mt-3 pt-3 border-t border-border-light flex items-center gap-2 text-sm text-text-secondary">
          <Users className="h-4 w-4" />
          <span>
            {slot.minParticipants}
            {slot.maxParticipants && ` a ${slot.maxParticipants}`} participantes
          </span>
        </div>
      </div>
    </div>
  )

  return (
    <section>
      <h2 className="text-xl font-bold text-text-primary mb-3">{title}</h2>
      <div className="space-y-4">
        {/* Tabs de Modalidade */}
        <div className="flex flex-wrap gap-2">
          {data.map(modality => (
            <button
              key={modality.modality}
              type="button"
              onClick={() => setActiveModality(modality.modality)}
              className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
                selectedModality === modality.modality
                  ? 'bg-brand-primary text-white border-brand-primary shadow-sm'
                  : 'text-text-secondary border-border-light hover:text-text-primary hover:border-border-strong bg-white'
              }`}
            >
              {modalitiesManager.getModalityDescription(modality.modality)}
            </button>
          ))}
        </div>

        {/* Grid de dias com todos os horários visíveis */}
        {selectedModalityData && weekdayGroups.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {weekdayGroups.map(dayGroup => (
              <div key={dayGroup.weekday} className="space-y-3">
                <div className="flex items-center gap-2">
                  <h3 className="text-sm font-semibold text-text-primary">
                    {getDayOfWeekName(dayGroup.weekday)}
                  </h3>
                  <span className="text-xs text-text-secondary">
                    {dayGroup.slots.length} {dayGroup.slots.length === 1 ? 'horário' : 'horários'}
                  </span>
                </div>
                <div className="space-y-2">
                  {dayGroup.slots.map(slot => renderSlot(slot))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-sm text-text-secondary py-4">
            Nenhum horário disponível para esta modalidade.
          </div>
        )}
      </div>
    </section>
  )
}
